/**
 * RiverOS SDK Implementation
 * Provides the riverOS API to tool handlers
 */

import type {
  RiverOS,
  ToolResult,
  DocumentContent,
  DocumentCreateOptions,
  SearchResult,
  Entity,
  EntityCreateOptions,
  LLMCompleteOptions,
  CommentCreateOptions,
  Comment,
  ProgressUpdate,
  FetchOptions,
  CacheOptions
} from './types';

import { createFileLogger } from '../utils/logger';
import fetchService from '../services/fetchService';
import { API_BASE_URL } from '../api/config';
import { 
  buildPositionMap, 
  findNodeForPosition, 
  findNodesInRange,
  extractTextFromNode
} from './utils/documentPositions';

const log = createFileLogger('RIVER_SDK');

interface CreateRiverOSOptions {
  userId: string;
  spaceId: string;
  threadId: string;
  executionId: string;
  traceId?: string;
  userPlan: 'free' | 'basic' | 'standard' | 'premium' | 'plus' | 'ultra' | 'pro';
  userEmail: string;
  toolId: string;
  onProgress?: (progress: ProgressUpdate) => void;
}

export function createRiverOS(options: CreateRiverOSOptions): RiverOS {
  const {
    userId,
    spaceId,
    threadId,
    executionId,
    traceId,
    userPlan,
    userEmail,
    toolId,
    onProgress
  } = options;

  const riverOS: RiverOS = {
    // Documents API
    docs: {
      async read(documentId: string): Promise<DocumentContent> {
        log.log(`[docs.read] ${documentId}`);
        
        try {
          const commandStore = await import('../stores/commandRegistryStore');
          const result: any = await commandStore.useCommandRegistryStore.getState().executeCommand(
            'doc_read_v2', 
            { id: documentId, selector: { all: true } },  // ✅ Use 'id' not 'documentId', add selector
            {
              userId,
              spaceId,
              threadId,
              executionId,
              traceId
            } as any
          );
          
          log.log('[docs.read] Command result:', {
            hasResult: !!result,
            status: result?.status,
            hasData: !!result?.data,
            dataKeys: result?.data ? Object.keys(result.data) : [],
            hasMarkdown: !!result?.data?.markdown,
            markdownLength: result?.data?.markdown?.length,
            hasNodes: !!result?.data?.nodes,
            nodesLength: result?.data?.nodes?.length
          });
          
          const content = result?.data?.markdown || result?.data?.content || result?.data?.text || '';
          const nodes = result?.data?.nodes || [];
          const metadata = result?.data?.metadata || {};
          
          log.log('[docs.read] Extracted:', {
            contentLength: content.length,
            nodesLength: nodes.length,
            metadataKeys: Object.keys(metadata)
          });
          
          return {
            content,
            metadata,
            nodes
          };
        } catch (error) {
          log.error('[docs.read] Error:', error);
          throw error;
        }
      },

      async create(opts: DocumentCreateOptions): Promise<{ id: string }> {
        log.log(`[docs.create] ${opts.name}`);
        const commandStore = await import('../stores/commandRegistryStore');
        const result: any = await commandStore.useCommandRegistryStore.getState().executeCommand('docs_create', {
          name: opts.name,
          content: opts.content,
          folderId: opts.folderId
        }, {
          userId,
          spaceId,
          threadId,
          executionId,
          traceId
        } as any);
        
        return { id: result.data?.entityId || result.data?.id };
      },

      async write(documentId: string, content: string): Promise<void> {
        log.log(`[docs.write] ${documentId}`);
        const commandStore = await import('../stores/commandRegistryStore');
        await commandStore.useCommandRegistryStore.getState().executeCommand('doc_write', {
          documentId,
          content
        }, {
          userId,
          spaceId,
          threadId,
          executionId,
          traceId
        } as any);
      },

      async search(query: string, opts?: { documentId?: string; limit?: number }): Promise<SearchResult[]> {
        log.log(`[docs.search] ${query}`);
        const commandStore = await import('../stores/commandRegistryStore');
        const result: any = await commandStore.useCommandRegistryStore.getState().executeCommand('doc_search_v2', {
          query,
          documentId: opts?.documentId,
          limit: opts?.limit || 10
        }, {
          userId,
          spaceId,
          threadId,
          executionId,
          traceId
        } as any);
        
        return result.data?.results || [];
      }
    },

    // Entities API
    entities: {
      async get(entityId: string): Promise<Entity> {
        log.log(`[entities.get] ${entityId}`);
        const commandStore = await import('../stores/commandRegistryStore');
        const result: any = await commandStore.useCommandRegistryStore.getState().executeCommand('entity_get', { entityId }, {
          userId,
          spaceId,
          threadId,
          executionId,
          traceId
        } as any);
        
        return result.data?.entity;
      },

      async create(entity: EntityCreateOptions): Promise<Entity> {
        log.log(`[entities.create] ${entity.type} - ${entity.name}`);
        const entityService = (await import('../services/entityService')).entityService;
        
        const created: any = await entityService.create({
          entityType: entity.type,
          name: entity.name,
          skeleton: entity.skeleton
        });
        
        // Add to folder if specified
        if (entity.folderId) {
          const commandStore = await import('../stores/commandRegistryStore');
          await commandStore.useCommandRegistryStore.getState().executeCommand('entity_move', {
            entityId: created._id,
            targetFolderId: entity.folderId
          }, {} as any);
        }
        
        return created as Entity;
      },

      async update(entityId: string, updates: Partial<Entity>): Promise<Entity> {
        log.log(`[entities.update] ${entityId}`);
        const commandStore = await import('../stores/commandRegistryStore');
        const result: any = await commandStore.useCommandRegistryStore.getState().executeCommand('entity_update', {
          entityId,
          updates
        }, {
          userId,
          spaceId,
          threadId,
          executionId,
          traceId
        } as any);
        
        return result.data?.entity;
      },

      async delete(entityId: string): Promise<void> {
        log.log(`[entities.delete] ${entityId}`);
        const commandStore = await import('../stores/commandRegistryStore');
        await commandStore.useCommandRegistryStore.getState().executeCommand('entity_delete', { entityId }, {
          userId,
          spaceId,
          threadId,
          executionId,
          traceId
        } as any);
      },

      async search(query: string, opts?: { type?: string; folderId?: string }): Promise<Entity[]> {
        log.log(`[entities.search] ${query}`);
        const commandStore = await import('../stores/commandRegistryStore');
        const result: any = await commandStore.useCommandRegistryStore.getState().executeCommand('entity_find', {
          query,
          type: opts?.type,
          folderId: opts?.folderId
        }, {
          userId,
          spaceId,
          threadId,
          executionId,
          traceId
        } as any);
        
        return result.data?.entities || [];
      }
    },

    // LLM API
    llm: {
      async complete(opts: LLMCompleteOptions): Promise<string | any> {
        log.log(`[llm.complete] model=${opts.model}`, { hasTools: !!opts.tools });
        
        const authToken = localStorage.getItem('authToken');
        
        const response = await fetch(`${API_BASE_URL}/v2/ai/chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
          },
          body: JSON.stringify({
            query: opts.prompt,
            model: opts.model,
            temperature: opts.temperature || 0.2,
            max_tokens: opts.maxTokens,
            tools: opts.tools || undefined,
            context: {
              threadId: threadId,
              spaceId: spaceId
            },
            conversation_history: [],
            use_rag: false
          })
        });

        if (!response.ok) {
          log.error(`[llm.complete] Request failed:`, response.status);
          
          // Handle 403 - out of credits
          if (response.status === 403) {
            const { default: useUsageLimitsStore } = await import('../stores/usageLimitsStore');
            const { default: useUserStore } = await import('../stores/userStore');
            const currentPlanName = useUserStore.getState().plan?.plan || 'free';

            useUsageLimitsStore.getState().showUpgradeModal({
              limitType: 'monthly_cost',
              currentUsage: { used: 50, max: 50, unit: 'dollars' },
              planName: currentPlanName,
              title: 'Budget Exhausted',
              description: 'You\'ve used your AI budget for this month.',
              message: 'Upgrade your plan for more AI budget and unlimited access to tools like Style Check.'
            });
            
            throw new Error('Budget exhausted - please upgrade to continue');
          }
          
          throw new Error(`LLM request failed: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let fullResponse = '';
        const toolCalls: any[] = [];
        let lineCount = 0;
        const eventTypes = new Set<string>();

        log.log('[llm.complete] Starting stream processing...');

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            log.log('[llm.complete] Stream done');
            break;
          }
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n').filter(l => l.trim());
          lineCount += lines.length;
          
          for (const line of lines) {
            try {
              const data = JSON.parse(line);
              eventTypes.add(data.type);
              
              // Log ALL event types for debugging
              if (data.type && !['content_block_delta', 'text'].includes(data.type)) {
                log.log(`[llm.complete] Event:`, data.type, data);
              }
              
              // Handle text content
              if (data.type === 'content_block_delta' && data.delta?.text) {
                const text = data.delta.text;
                if (opts.onChunk) opts.onChunk(text);
                fullResponse += text;
              }
              // Handle tool input deltas
              else if (data.type === 'content_block_delta' && data.delta?.type === 'input_json_delta') {
                // Find the tool call by index and accumulate JSON
                const tool = toolCalls[data.index];
                if (tool) {
                  tool.partialJson = (tool.partialJson || '') + (data.delta.partial_json || '');
                }
              }
              // Handle backend text format
              else if (data.type === 'text' && data.content) {
                const text = data.content;
                if (opts.onChunk) opts.onChunk(text);
                fullResponse += text;
              }
              // Tool call starts - capture the tool info
              else if (data.type === 'content_block_start' && data.content_block?.type === 'tool_use') {
                const toolInfo = {
                  id: data.content_block.id,
                  name: data.content_block.name,
                  index: data.index
                };
                log.log(`[llm.complete] ✅ TOOL START:`, toolInfo);
                toolCalls[data.index] = { ...toolInfo, input: {}, partialJson: '' };
              }
              // Tool block stops - parse complete input
              else if (data.type === 'content_block_stop') {
                const tool = toolCalls[data.index];
                if (tool && tool.partialJson) {
                  try {
                    tool.input = JSON.parse(tool.partialJson);
                    log.log(`[llm.complete] ✅ TOOL COMPLETE:`, tool.name, tool.input);
                    if (opts.onToolCall) {
                      log.log(`[llm.complete] Calling onToolCall callback`);
                      opts.onToolCall({ name: tool.name, input: tool.input });
                    }
                  } catch (e) {
                    log.error(`[llm.complete] Failed to parse tool input:`, e);
                  }
                }
              }
            } catch (parseError) {
              // Silent - many lines won't be JSON
            }
          }
        }
        
        log.log(`[llm.complete] Complete:`, { 
          textLength: fullResponse.length, 
          toolCalls: toolCalls.length,
          linesProcessed: lineCount,
          eventTypes: Array.from(eventTypes)
        });
        
        // If tools were provided and we got tool calls, return structured result
        if (opts.tools && toolCalls.length > 0) {
          return {
            text: fullResponse,
            toolCalls
          };
        }
        
        return fullResponse;
      },

      async embed(text: string): Promise<number[]> {
        log.log(`[llm.embed] ${text.slice(0, 50)}...`);
        // TODO: Implement embedding API
        throw new Error('Embedding API not yet implemented');
      }
    },

    // Comments API
    comments: {
      async create(opts: CommentCreateOptions): Promise<{ threadId: string }> {
        log.log(`[comments.create] doc=${opts.documentId} node=${opts.nodeId}`);
        const commandStore = await import('../stores/commandRegistryStore');
        const result: any = await commandStore.useCommandRegistryStore.getState().executeCommand('docs_add_comment', {
          documentId: opts.documentId,
          nodeId: opts.nodeId,
          content: opts.content,
          metadata: opts.metadata
        }, {
          userId,
          spaceId,
          threadId,
          executionId,
          traceId
        } as any);
        
        return { threadId: result.data?.threadId };
      },

      async get(threadId: string): Promise<Comment[]> {
        log.log(`[comments.get] ${threadId}`);
        // TODO: Implement get comments
        throw new Error('Get comments API not yet implemented');
      },

      async reply(threadId: string, content: string): Promise<void> {
        log.log(`[comments.reply] ${threadId}`);
        // TODO: Implement reply to comment
        throw new Error('Reply to comment API not yet implemented');
      }
    },

    // Progress API
    progress: {
      update(update: ProgressUpdate): void {
        log.log(`[progress.update]`, update);
        if (onProgress) {
          // Format for ConversationManager (matches what it expects)
          onProgress({
            type: 'streaming_update',
            toolUseId: executionId,
            activity: update.message || update.phase,
            progress: update
          } as any);
        }
      },

      stream(chunk: string): void {
        log.log(`[progress.stream] ${chunk.slice(0, 50)}...`);
        if (onProgress) {
          onProgress({
            type: 'streaming_update',
            toolUseId: executionId,
            message: chunk
          } as any);
        }
      }
    },

    // Fetch API
    fetch: async (url: string, opts?: FetchOptions): Promise<Response> => {
      log.log(`[fetch] ${url}`);
      
      // TODO: Implement rate limiting
      const rateLimiter = opts?.rateLimiter;
      const maxRetries = opts?.maxRetries || 0;
      
      const fetchOptions: RequestInit = {
        method: opts?.method || 'GET',
        headers: opts?.headers,
        body: opts?.body ? JSON.stringify(opts.body) : undefined
      };
      
      let lastError: Error | null = null;
      
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const response = await fetch(url, fetchOptions);
          return response;
        } catch (error) {
          lastError = error as Error;
          if (attempt < maxRetries) {
            // Exponential backoff
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
          }
        }
      }
      
      throw lastError || new Error('Fetch failed');
    },

    // Cache API
    cache: {
      async get<T>(key: string): Promise<T | null> {
        const scopedKey = `tool:${toolId}:${key}`;
        const value = localStorage.getItem(scopedKey);
        return value ? JSON.parse(value) : null;
      },

      async set<T>(key: string, value: T, opts?: CacheOptions): Promise<void> {
        const scopedKey = `tool:${toolId}:${key}`;
        localStorage.setItem(scopedKey, JSON.stringify(value));
        
        // TODO: Implement TTL with expiration
        if (opts?.ttl) {
          const expiration = Date.now() + (opts.ttl * 1000);
          localStorage.setItem(`${scopedKey}:exp`, expiration.toString());
        }
      },

      async delete(key: string): Promise<void> {
        const scopedKey = `tool:${toolId}:${key}`;
        localStorage.removeItem(scopedKey);
        localStorage.removeItem(`${scopedKey}:exp`);
      },

      async clear(): Promise<void> {
        const prefix = `tool:${toolId}:`;
        const keys = Object.keys(localStorage);
        for (const key of keys) {
          if (key.startsWith(prefix)) {
            localStorage.removeItem(key);
          }
        }
      }
    },

    // Backend API
    backend: {
      async call<T>(endpoint: string, data?: any): Promise<T> {
        log.log(`[backend.call] ${endpoint}`);
        const url = `${API_BASE_URL}/tools/${toolId}${endpoint}`;
        const response = await fetchService(url, {
          method: 'POST',
          body: JSON.stringify(data)
        });
        return response as T;
      }
    },

    // Context
    user: {
      id: userId,
      plan: userPlan,
      email: userEmail
    },

    space: {
      id: spaceId
    },

    thread: {
      id: threadId
    },

    execution: {
      id: executionId,
      traceId
    },

    // Document Utilities
    utils: {
      buildPositionMap,
      findNodeForPosition,
      findNodesInRange,
      extractTextFromNode
    },

    // Tools API - for calling other tools programmatically
    tools: {
      async execute<T = any>(toolId: string, params: any): Promise<T> {
        log.log(`[tools.execute] ${toolId}`, params);
        
        try {
          // Use command registry for proper execution with full context
          const commandStore = await import('../stores/commandRegistryStore');
          const result = await commandStore.useCommandRegistryStore.getState().executeCommand(
            toolId,
            params,
            {
              userId,
              spaceId,
              threadId,
              executionId,
              traceId
            } as any
          ) as any;
          
          log.log(`[tools.execute] ${toolId} completed:`, {
            status: result?.status,
            hasData: !!result?.data
          });
          
          return result as T;
        } catch (error) {
          log.error(`[tools.execute] ${toolId} failed:`, error);
          throw error;
        }
      }
    },

    // Result Helpers
    success(opts: { message: string; data?: any }): ToolResult {
      return {
        success: true,
        message: opts.message,
        data: opts.data
      };
    },

    error(message: string): ToolResult {
      return {
        success: false,
        message,
        error: message
      };
    }
  };

  return riverOS;
}

